import {
  type ExportProgress,
  type PaymentTerm,
  type ShippingMethod,
  type ShippingTerm,
  type Vendor,
  type VendorAttachment,
} from '@webapp/models'
import { ApiClient } from '@webapp/services/api'

export const ENDPOINT = '/api/v3/vendors/'

export const VENDORS_URL = ENDPOINT
export const DEFAULT_VENDORS_URL = `${ENDPOINT}default/`

export interface CreateVendorSubmission {
  name: Vendor['name']
  email: string[]
  external_id?: Vendor['external_id']
  contact?: Vendor['contact']
  comments?: Vendor['comments']
  addressLineOne?: Vendor['addressLineOne']
  addressLineTwo?: Vendor['addressLineTwo']
  city?: Vendor['city']
  country?: Vendor['country']
  payment_term_ref?: PaymentTerm['id']
  phoneOne?: Vendor['phoneOne']
  phoneTwo?: Vendor['phoneTwo']
  fax?: Vendor['fax']
  postalCode?: Vendor['postalCode']
  preferred?: boolean
  shipping_method_ref?: ShippingMethod['id']
  shipping_term_ref?: ShippingTerm['id']
  state_province?: Vendor['state_province']
  type?: Vendor['type']
  url?: Vendor['url']
  po_pdf_labels?: Vendor['po_pdf_labels']
  default_payment_method?: Vendor['default_payment_method']
  tax?: Vendor['tax']
  is_auto_email_po_enabled?: boolean
}

export interface UpdateVendorSubmission extends CreateVendorSubmission {
  id: Vendor['id']
}

export interface UpdateVendorAttachmentsSubmission {
  attachments: VendorAttachment['uuid'][]
}

export interface ExportVendorSchema {
  export_email: boolean
}

export class VendorsAPI {
  static readonly createVendor = (data: CreateVendorSubmission) => {
    return ApiClient.post<CreateVendorSubmission, Vendor>(VENDORS_URL, data)
  }

  static readonly updateVendor = (data: UpdateVendorSubmission) => {
    return ApiClient.put<UpdateVendorSubmission, Vendor>(
      `${VENDORS_URL}${data.id}/`,
      data
    )
  }

  static readonly patchVendor = (
    vendorId: Vendor['id'],
    data: Partial<UpdateVendorSubmission>
  ) => {
    return ApiClient.patch<Partial<UpdateVendorSubmission>, Vendor>(
      `${VENDORS_URL}${vendorId}/`,
      data
    )
  }

  static readonly getVendorDetail = (vendorId: Vendor['id']) => {
    return ApiClient.get<Vendor>(`${VENDORS_URL}${vendorId}/`)
  }

  static readonly createVendorAttachments = (
    vendorId: Vendor['id'],
    data: UpdateVendorAttachmentsSubmission
  ) => {
    return ApiClient.post<UpdateVendorAttachmentsSubmission, Vendor>(
      `${VENDORS_URL}${vendorId}/attachments/`,
      data
    )
  }

  static readonly exportVendors = (data: ExportVendorSchema) => {
    return ApiClient.post<ExportVendorSchema, ExportProgress>(
      `${VENDORS_URL}export/`,
      data
    )
  }

  static readonly deleteVendor = (vendorId: Vendor['id']) => {
    return ApiClient.delete(`${VENDORS_URL}${vendorId}/`)
  }

  static readonly getDefaultVendors = (params) => {
    return ApiClient.get<Vendor[]>(DEFAULT_VENDORS_URL, { params })
  }
}
